import store from '../redux/store';
import { update_assets_data } from '../redux/Slice/gridSlice';
import { safeHistoryCloneOrEmpty } from './safeHistoryClone';
import { buildTextReduxAssetRecord } from './textAssetUtils';
import { suppressGridHistory } from './gridHistorySuppress';

const ASSET_DELETE_TYPE_TO_KEY = {
  mesh: 'mesh',
  image: 'image',
  video: 'video',
  audio: 'audio',
  text: 'text',
  hotspot: 'hotspot',
  empty: 'empty',
};

export function isInternalAssetId(value) {
  if (!value || typeof value !== 'string') return false;
  if (value.startsWith('temp-') || value.startsWith('temp_')) return true;
  if (/^text-\d{10,}$/.test(value)) return true;
  return false;
}

export function collectAssetLookupIds(item) {
  if (!item) return [];
  const ids = new Set();
  const add = (value) => {
    if (value != null && value !== '') ids.add(String(value));
  };

  add(item._id);
  add(item.id);
  add(item.url);
  add(item.name);
  add(item.temp_file_name);
  add(item.material_settings?.id);
  add(item.userData?.material_settings?.id);
  add(item.text_material?.id);
  add(item.userData?.text_material?.id);

  return [...ids];
}

export function recordsReferToSameAsset(a, b) {
  if (!a || !b) return false;
  const idsA = collectAssetLookupIds(a);
  const idsB = collectAssetLookupIds(b);
  return idsA.some((id) => idsB.includes(id));
}

export function hasDisplayName(record) {
  if (!record) return false;
  if (String(record.file_name || '').trim()) return true;
  if (String(record.asset_file_name || '').trim()) return true;

  const fallback =
    record.userData?.file_name ||
    record.userData?.asset_file_name ||
    record.name ||
    record.temp_file_name;
  return !!fallback && !isInternalAssetId(fallback);
}

function pickDisplayName(...sources) {
  for (const source of sources) {
    if (!source) continue;

    const candidates = [
      source.file_name,
      source.asset_file_name,
      source.userData?.file_name,
      source.userData?.asset_file_name,
      !isInternalAssetId(source.name) ? source.name : null,
    ];

    for (const value of candidates) {
      const trimmed = String(value || '').trim();
      if (trimmed && !isInternalAssetId(trimmed)) return trimmed;
    }
  }

  return null;
}

function normalizeShapeType(value) {
  if (!value || typeof value !== 'string') return null;
  return value.trim().toLowerCase();
}

function mergeDefinedFields(base, incoming) {
  const merged = { ...(base || {}) };
  Object.entries(incoming || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      merged[key] = value;
    }
  });
  return merged;
}

function mergeRecordPreferringRicher(base, incoming) {
  const merged = mergeDefinedFields(base, incoming);
  const displayName = pickDisplayName(incoming, base);

  if (displayName) {
    merged.file_name = displayName;
    merged.asset_file_name =
      incoming?.asset_file_name || base?.asset_file_name || displayName;
  }

  if (base?._id && !isInternalAssetId(base._id)) merged._id = base._id;
  if (base?.id && !isInternalAssetId(base.id)) merged.id = base.id;
  merged.temp_file_name = incoming?.temp_file_name || base?.temp_file_name;
  merged.url = incoming?.url || base?.url;
  merged.name = incoming?.name || base?.name;
  merged.mesh_code = incoming?.mesh_code ?? base?.mesh_code ?? merged.mesh_code;
  merged.glb_type = incoming?.glb_type ?? base?.glb_type ?? merged.glb_type;
  merged.isShapes = incoming?.isShapes ?? base?.isShapes ?? merged.isShapes;
  merged.shape_type =
    incoming?.shape_type ??
    base?.shape_type ??
    normalizeShapeType(merged.mesh_code ?? base?.mesh_code);

  return merged;
}

function dedupeAssetListByLookup(list) {
  const result = [];
  (list || []).forEach((item) => {
    const index = result.findIndex((existing) =>
      recordsReferToSameAsset(existing, item)
    );
    if (index === -1) {
      result.push(item);
      return;
    }
    result[index] = mergeRecordPreferringRicher(result[index], item);
  });
  return result;
}

function buildSceneAssetRecord(item) {
  if (!item) return item;
  if (item.isObject3D || item.isMesh || item.isGroup) {
    const textRecord =
      item.deleteType === 'text' ? buildTextReduxAssetRecord(item) : null;
    const displayName = pickDisplayName(item, item.userData);
    const meshCode = item.mesh_code ?? item.userData?.mesh_code;
    const shapeType =
      item.shape_type ??
      item.userData?.shape_type ??
      normalizeShapeType(meshCode);
    return safeHistoryCloneOrEmpty(
      textRecord || {
        _id: item._id ?? item.id ?? item.name ?? item.url,
        id: item.id ?? item._id ?? item.name ?? item.url,
        name: item.name,
        url: item.url,
        temp_file_name: item.temp_file_name,
        file_name: displayName,
        asset_file_name:
          item.asset_file_name || displayName || item.userData?.asset_file_name,
        deleteType: item.deleteType,
        objType: item.objType,
        glb_type: item.glb_type ?? item.userData?.glb_type,
        mesh_code: meshCode,
        shape_type: shapeType,
        isShapes:
          item.isShapes === true ||
          item.userData?.isShapes === true ||
          item.glb_type === 'shapes' ||
          item.userData?.glb_type === 'shapes',
        position_x: item.position?.x,
        position_y: item.position?.y,
        position_z: item.position?.z,
        rotation_x: item.rotation?.x,
        rotation_y: item.rotation?.y,
        rotation_z: item.rotation?.z,
        scale_x: item.scale?.x,
        scale_y: item.scale?.y,
        scale_z: item.scale?.z,
        visible: item.visible,
        events: item.events,
        material_settings: item.userData?.material_settings,
        text_material: item.userData?.text_material,
        userData: safeHistoryCloneOrEmpty(item.userData),
      }
    );
  }

  const displayName = pickDisplayName(item, item.userData);
  if (displayName && !item.file_name) {
    return safeHistoryCloneOrEmpty({
      ...item,
      file_name: displayName,
      asset_file_name: item.asset_file_name || displayName,
    });
  }

  return safeHistoryCloneOrEmpty(item);
}

function serializeAssetsData(assetsData) {
  if (!assetsData || typeof assetsData !== 'object') return {};
  const output = {};
  Object.entries(assetsData).forEach(([type, list]) => {
    output[type] = Array.isArray(list)
      ? list.map((item) => buildSceneAssetRecord(item))
      : [];
  });
  return output;
}

function serializeAssetListForCompare(list) {
  return JSON.stringify(
    (list || []).map((item) => buildSceneAssetRecord(item))
  );
}

function shouldReconcileRootSceneAsset(obj, scene) {
  if (!obj?.deleteType || !ASSET_DELETE_TYPE_TO_KEY[obj.deleteType]) return false;
  if (obj.userData?.isEditorLightIcon || obj.userData?.isHdriBackground) {
    return false;
  }
  if (obj.isCamera || obj.isLight) return false;
  if (typeof obj.name === 'string' && obj.name.endsWith('-helper')) return false;
  return !obj.parent || obj.parent === scene;
}

function collectSceneAssetsByType(scene) {
  const sceneByType = {};
  if (!scene) return sceneByType;

  scene.traverse((obj) => {
    if (!shouldReconcileRootSceneAsset(obj, scene)) return;

    const typeKey = ASSET_DELETE_TYPE_TO_KEY[obj.deleteType];
    if (!typeKey) return;

    if (!sceneByType[typeKey]) sceneByType[typeKey] = [];
    sceneByType[typeKey].push({
      obj,
      record: buildSceneAssetRecord(obj),
    });
  });

  return sceneByType;
}

function sceneContainsAsset(scene, item) {
  if (!scene || !item) return false;
  return collectAssetLookupIds(item).some((id) => !!scene.getObjectByName(id));
}

export function mergeAssetsDataWithScene(assetsData, scene, options = {}) {
  const { pruneOrphans = false } = options;
  const merged = serializeAssetsData(assetsData);
  const sceneByType = collectSceneAssetsByType(scene);

  Object.values(ASSET_DELETE_TYPE_TO_KEY).forEach((typeKey) => {
    let list = dedupeAssetListByLookup(merged[typeKey] || []);
    const sceneEntries = sceneByType[typeKey] || [];
    const matchedIndexes = new Set();

    sceneEntries.forEach(({ obj, record }) => {
      const index = list.findIndex(
        (item) => recordsReferToSameAsset(item, record) || recordsReferToSameAsset(item, obj)
      );

      if (index >= 0) {
        matchedIndexes.add(index);
        list[index] = mergeRecordPreferringRicher(list[index], record);
        return;
      }

      if (!hasDisplayName(record)) return;
      list.push(record);
      matchedIndexes.add(list.length - 1);
    });

    if (pruneOrphans) {
      list = list.filter((item, index) => {
        if (matchedIndexes.has(index)) return true;

        const hasSceneMatch = sceneEntries.some(
          ({ obj, record }) =>
            recordsReferToSameAsset(item, record) || recordsReferToSameAsset(item, obj)
        );
        if (hasSceneMatch) return true;

        if (scene && sceneContainsAsset(scene, item)) return true;
        return hasDisplayName(item);
      });
    }

    merged[typeKey] = dedupeAssetListByLookup(list);
  });

  return merged;
}

/** Keep AppLeftPanel assets_data aligned with objects visible in the grid. */
export function reconcileAssetsDataFromScene(scene, options = {}) {
  if (!scene) return false;

  const current = store.getState().assets_object?.assets_data || {};
  const assetsLoading = store.getState().assets_object?.assets_loading;
  const merged = mergeAssetsDataWithScene(current, scene, {
    pruneOrphans:
      options.pruneOrphans !== undefined
        ? options.pruneOrphans
        : !assetsLoading,
  });
  let changed = false;

  Object.values(ASSET_DELETE_TYPE_TO_KEY).forEach((typeKey) => {
    const before = serializeAssetListForCompare(current[typeKey]);
    const after = serializeAssetListForCompare(merged[typeKey]);
    if (before !== after) {
      changed = true;
    }
  });

  if (!changed) return false;

  const endSuppress = suppressGridHistory();
  try {
    Object.values(ASSET_DELETE_TYPE_TO_KEY).forEach((typeKey) => {
      const before = serializeAssetListForCompare(current[typeKey]);
      const after = serializeAssetListForCompare(merged[typeKey]);
      if (before !== after) {
        store.dispatch(
          update_assets_data({ type: typeKey, data: merged[typeKey] || [] })
        );
      }
    });
  } finally {
    endSuppress();
  }

  return true;
}
